/**
 * Special Forces Sustainment - 6 Week Tactical Program
 * 
 * High-intensity tactical training program built to develop:
 * - Relative strength
 * - Muscular endurance  
 * - Work capacity
 * - Ruck conditioning under fatigue
 * 
 * Best used to build quickly or work up to the Operator 13 week program.
 * 
 * Structure:
 * - Week 1: Assessment Week
 * - Weeks 2-5: Training Cycle (Build → Intensification → Peak)
 * - Week 6: Final Assessment Week
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'special-forces-sustainment-6week',
  name: 'Special Forces Sustainment',
  description: 'High-intensity tactical training program built to develop relative strength, muscular endurance, work capacity, and ruck conditioning under fatigue. Blends strength work, operator-style conditioning, ruck work, and finishers. Best used to build quickly or work up to the Operator 13 week.',
  level: 'operator',
  duration: 6,
  daysPerWeek: 6,
  goals: ['tactical-performance', 'strength', 'endurance', 'work-capacity', 'ruck'],
  difficulty: 'elite',
  version: '1.0.0',
  createdAt: new Date('2026-01-04'),
  updatedAt: new Date('2026-01-04'),
  isActive: true,
  assessmentFormat: 'sf-fitness-test', // Redirect to /assessment/{day} for SF-style fitness testing
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'assessment',
      name: 'Initial Assessment',
      focus: 'Baseline testing and evaluation',
      intensityMultiplier: 1.0,
      volumeMultiplier: 0.5
    },
    {
      weeks: [2, 3],
      type: 'accumulation',
      name: 'Build Phase',
      focus: 'Volume accumulation and work capacity',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    {
      weeks: [4, 5],
      type: 'intensification',
      name: 'Intensification',
      focus: 'Increased intensity and ruck distance',
      intensityMultiplier: 1.1,
      volumeMultiplier: 0.95
    },
    {
      weeks: [6],
      type: 'assessment',
      name: 'Final Assessment',
      focus: 'Performance testing and validation',
      intensityMultiplier: 1.0,
      volumeMultiplier: 0.5
    }
  ]
};

// ============================================================================
// ASSESSMENT WEEK TEMPLATES (Weeks 1 & 6)
// ============================================================================

// SF/Ranger-standard assessment battery. Kept IDENTICAL to the 13-Week
// Operator program's testing week (operatorStrength13WeekReal.ts) — both
// programs run the same standard so scores are comparable across them:
//   Day 1  Deadlift 3RM · Bench bodyweight max reps · Plank · HR Push-Ups · 1.5mi run
//   Day 3  6-mile ruck (35 lb)
//   Day 5  Tactical AMRAP
//   Day 6  Back Squat / Bench / Deadlift 1RM
const assessmentDay1: WorkoutTemplateDay = {
  name: 'SF Assessment Day 1',
  sessionType: 'assessment',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'distance-time', details: 'Bike or row - easy pace' },
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Leg swings, arm circles, hip openers' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'bodyweight', details: 'Activate glutes and hamstrings' },
        { name: 'Push-Ups', sets: 2, reps: '10', format: 'bodyweight', details: 'Warm up chest and triceps' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Ramp the deadlift + bench to a heavy prep set before the max effort. Do not test cold.' },
      ],
    },
    {
      name: 'Strength Test',
      type: 'testing',
      exercises: [
        { name: 'Deadlift', sets: 1, reps: 3, intensity: 'Max', rest: '3-5 min', details: 'Heaviest triple, no bounce', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: 'Max', intensity: 'Bodyweight', rest: '3-5 min', details: 'Strict ROM, max reps at bodyweight', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Work Capacity Testing',
      type: 'testing',
      exercises: [
        { name: 'Plank', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Hold for max time', sessionType: 'assessment' },
        { name: 'Hand Release Push-Ups', sets: 1, reps: 'Max', format: 'bodyweight', details: 'Chest to ground, hands off', sessionType: 'assessment' },
        { name: '1.5 Mile Run', duration: 'For Time', format: 'distance-time', details: 'Record time', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Walk', duration: '5 min', format: 'distance-time', details: 'Easy pace cool down' },
        { name: 'Quad Stretch', duration: '1 min', format: 'recovery', details: '30s each leg' },
        { name: 'Hamstring Stretch', duration: '1 min', format: 'recovery', details: '30s each leg' },
        { name: 'Chest Stretch', duration: '1 min', format: 'recovery', details: 'Doorway or partner stretch' },
        { name: 'Shoulder Stretch', duration: '1 min', format: 'recovery', details: 'Cross-body and overhead' },
      ],
    },
  ],
};

const assessmentDay3: WorkoutTemplateDay = {
  name: '6 Mile Ruck Test',
  sessionType: 'assessment',
  estimatedDuration: 120,
  blocks: [
    {
      name: 'Ruck Prep',
      type: 'prep',
      exercises: [
        { name: 'Walk', duration: '5 min', format: 'distance-time', details: 'Easy pace to warm up' },
        { name: 'Leg Swings', sets: 2, reps: '10', format: 'bodyweight', details: 'Forward/back and side-to-side' },
        { name: 'Ankle Circles', sets: 2, reps: '10', format: 'bodyweight', details: 'Each direction, each ankle' },
        { name: 'Bodyweight Squat', sets: 2, reps: '10', format: 'bodyweight', details: 'Focus on depth and mobility' },
        // 'prep', not 'bodyweight': Calf Raise resolves to a LOADED DB entry
        // (Seated Calf Raise, weight-reps), so declaring it bodyweight made the
        // app promise no load and then ask for one. It is a warm-up here, which
        // is what 'prep' already means — matching the identical line in the
        // other week's warm-up block below.
        { name: 'Calf Raise', sets: 2, reps: '15', format: 'prep', details: 'Prepare ankles for load' },
      ],
    },
    {
      name: 'Ruck Test',
      type: 'testing',
      exercises: [
        { name: '6 Mile Ruck', duration: 'For Time', format: 'ruck', details: '35 lb (before water)', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Walk', duration: '5 min', format: 'distance-time', details: 'Easy pace cool down' },
        { name: 'Hip Flexor Stretch', duration: '2 min', format: 'recovery', details: '1 min each side, kneeling lunge' },
        { name: 'Calf Stretch', duration: '2 min', format: 'recovery', details: '1 min each side, wall stretch' },
        { name: 'Quad Stretch', duration: '2 min', format: 'recovery', details: '1 min each side, standing' },
        { name: 'Hamstring Stretch', duration: '2 min', format: 'recovery', details: '1 min each side, seated or standing' },
      ],
    },
  ],
};

const assessmentDay5: WorkoutTemplateDay = {
  name: 'Tactical AMRAP',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'distance-time', details: 'Bike, row, or jog - easy pace' },
        { name: 'Arm Circles', sets: 2, reps: '10', format: 'bodyweight', details: 'Forward and backward' },
        { name: 'Leg Swings', sets: 2, reps: '10', format: 'bodyweight', details: 'Forward/back and side-to-side' },
        { name: 'Scap Pull-Ups', sets: 2, reps: '5', format: 'bodyweight', details: 'Activate lats and shoulders' },
        { name: 'Push-Ups', sets: 2, reps: '5', format: 'bodyweight', details: 'Warm up chest' },
        { name: 'Air Squat', sets: 2, reps: '10', format: 'bodyweight', details: 'Warm up legs' },
      ],
    },
    {
      name: 'Work Capacity AMRAP',
      type: 'testing',
      exercises: [
        { name: 'Tactical AMRAP', duration: '10 min', format: 'circuit', details: '5 Pull-Ups, 10 Push-Ups, 15 Squats, 200m Run (0.12 miles on treadmill) - AMRAP. Plate carrier optional. Passing: 4+ rounds', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Walk', duration: '5 min', format: 'distance-time', details: 'Easy pace cool down' },
        { name: 'Shoulder Stretch', duration: '1 min', format: 'recovery', details: 'Cross-body and overhead' },
        { name: 'Chest Stretch', duration: '1 min', format: 'recovery', details: 'Doorway stretch' },
        { name: 'Quad Stretch', duration: '1 min', format: 'recovery', details: '30s each leg' },
        { name: 'Hamstring Stretch', duration: '1 min', format: 'recovery', details: '30s each leg' },
      ],
    },
  ],
};

const assessmentDay6: WorkoutTemplateDay = {
  name: '1RM Max Testing',
  sessionType: 'assessment',
  estimatedDuration: 90,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'distance-time', details: 'Bike or row - easy pace' },
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Full body - legs, hips, shoulders' },
        { name: 'Glute Bridge', sets: 3, reps: '10', format: 'bodyweight', details: 'Activate posterior chain' },
        { name: 'Goblet Squat', sets: 3, reps: '8', format: 'weight-reps', details: 'Light weight, work up gradually' },
        { name: 'Push-Ups', sets: 3, reps: '10', format: 'bodyweight', details: 'Warm up chest and triceps' },
        { name: 'Band Pull-Apart', sets: 3, reps: '15', format: 'bodyweight', details: 'Activate rear delts and upper back' },
      ],
    },
    {
      name: '1RM Testing',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
        { name: 'Deadlift', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Walk', duration: '5 min', format: 'distance-time', details: 'Easy pace cool down' },
        { name: 'Hip Flexor Stretch', duration: '2 min', format: 'recovery', details: '1 min each side' },
        { name: 'Quad Stretch', duration: '2 min', format: 'recovery', details: '1 min each side' },
        { name: 'Hamstring Stretch', duration: '2 min', format: 'recovery', details: '1 min each side' },
        { name: 'Chest Stretch', duration: '1 min', format: 'recovery', details: 'Doorway stretch' },
        { name: 'Shoulder Stretch', duration: '1 min', format: 'recovery', details: 'Cross-body and overhead' },
        { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose or cat-cow' },
      ],
    },
  ],
};

// Rest Day Template (used between assessment days)
const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt', // Must be 'pt' or 'assessment' per WorkoutTemplateDay type
  estimatedDuration: 0,
  blocks: [
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Active Recovery', duration: '20-30 min', format: 'recovery', details: 'Light walk, mobility work, or complete rest' },
      ],
    },
  ],
};

// ============================================================================
// TRAINING WEEK TEMPLATES (Weeks 2-5)
// ============================================================================

const trainingDay1: WorkoutTemplateDay = {
  name: 'Strength + Volume',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '3:00', format: 'prep', details: 'Easy pace — raise core temp' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Full-body dynamic opener' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before benching' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep', details: 'Knee-friendly quad prime for goblet squats' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation for the KB swing' },
      ]
    },
    {
      name: 'Strength Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Kettlebell Goblet Squat', sets: 4, reps: '12', rest: '0:00', rpe: 7, format: 'superset' },
        { name: 'Bench Press', sets: 4, reps: '10', rest: '2:30', rpe: 7, format: 'superset', intensity: '70%', percentRM: 70, details: '70% 1RM' },
      ]
    },
    {
      name: 'Strength Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Kettlebell Swing', sets: 4, reps: '20', rest: '0:00', rpe: 7, format: 'superset' },
        { name: 'Treadmill Incline Walk', sets: 4, duration: '2:30', rest: '1:30', rpe: 6, format: 'distance-time', details: '10% incline, 3.5 mph' },
      ]
    },
    {
      name: 'Accessory Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Lateral Raise', sets: 3, reps: '15', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Face Pull', sets: 3, reps: '15', rest: '1:00', rpe: 6, format: 'superset', details: 'Rear delts + upper back — balances the pressing volume for shoulder health under load' },
      ]
    },
    {
      name: 'Accessory Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Preacher Curl', sets: 4, reps: '15', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Triceps Pressdown', sets: 4, reps: '15', rest: '1:00', rpe: 6, format: 'superset' },
      ]
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after squatting' },
        { name: 'Wall Pec Stretch', duration: '45-60s/side', format: 'recovery', details: 'Open the chest after pressing' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ]
    },
  ]
};

const trainingDay2: WorkoutTemplateDay = {
  name: 'Heavy Strength',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Wake the posterior chain for the deadlift' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep', details: 'Primes quads + patellar tendon to squat — knee-friendly' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before loading the deadlift' },
      ]
    },
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: '3-5', rest: '3:00', rpe: 8, format: 'weight-reps', details: 'HYBRID: work up to a heavy top triple @ RPE 8 (ramp weekly ~80% → ~90% by wk5), then 3 back-off sets of 5 @ ~70%. Weekly peaking exposure for the wk6 1RM test.' },
        { name: 'Deadlift', sets: 3, reps: '3-5', rest: '3:00', rpe: 8, format: 'weight-reps', details: 'HYBRID: heavy top triple @ RPE 8 (ramp toward ~90% by wk5), then 2 back-off sets of 5 @ ~70%.' },
      ]
    },
    {
      name: 'Accessory Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Walking Lunge', sets: 3, reps: '20', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Step-Ups', sets: 3, reps: '12', rest: '2:00', rpe: 6, format: 'superset' },
      ]
    },
    {
      name: 'Accessory Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Seated Calf Raise', sets: 3, reps: '20', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Split Squat Curl Combo', sets: 3, reps: '10', rest: '1:30', rpe: 6, format: 'superset', details: 'Per leg' },
      ]
    },
    {
      name: 'Finisher Superset',
      type: 'conditioning',
      format: 'superset',
      exercises: [
        { name: 'Farmer Carry', sets: 4, distance: '40m', rest: '0:00', rpe: 7, format: 'superset' },
        { name: 'Sled Push', sets: 4, distance: '40m', rest: '1:00', rpe: 7, format: 'superset' },
      ]
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '12', rest: '1:00', rpe: 6, format: 'weight-reps', details: 'Per side — anti-rotation trunk stability for bracing under load' },
      ]
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Decompress hip flexors + quads after squatting' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Spinal decompression + grip flush after pulls/carries' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate after the heavy work' },
      ]
    },
  ]
};

const trainingDay3: WorkoutTemplateDay = {
  name: 'Conditioning',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Jog or bike — build to a light sweat' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Front-back + lateral — dynamic hips' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before sprinting' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine' },
        { name: 'Strides', sets: 4, distance: '40m', format: 'prep', details: 'Build 60% → 80% → 90%. Prepping the tissue before all-out is how you avoid a torn hamstring.' },
      ]
    },
    {
      name: '30/60s Intervals',
      type: 'conditioning',
      exercises: [
        { name: '30/60s Sprint', sets: 5, duration: '0:30', rest: '1:00', rpe: 8, format: 'cardio', details: '30s ALL OUT sprint, 60s walk. Treadmill or outside.' },
      ]
    },
    {
      name: '60/120s Intervals',
      type: 'conditioning',
      exercises: [
        { name: '60/120s Sprint', sets: 5, duration: '1:00', rest: '2:00', rpe: 9, format: 'cardio', details: '60s ALL OUT sprint, 120s walk. Treadmill or outside.' },
      ]
    },
    {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Treadmill Incline Walk', sets: 1, duration: '10:00', format: 'distance-time', details: '3% incline, 2.5 mph — flush the legs down' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Hamstrings take the brunt of sprinting' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate the sprint stress' },
      ]
    },
  ]
};

const trainingDay4: WorkoutTemplateDay = {
  name: 'Upper Body',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower — warm the upper back' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap retraction + rear delt' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Rotator cuff prime before pressing' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Serratus + scap control for pressing' },
        { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'Thoracic mobility for press + row' },
      ]
    },
    {
      name: 'Strength Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Bench Press', sets: 4, reps: '10', rest: '0:00', rpe: 7, format: 'superset', intensity: '65%', percentRM: 65, details: '65% Bench 1RM' },
        { name: 'Barbell Row', sets: 4, reps: '12', rest: '2:30', rpe: 7, format: 'superset', intensity: '60%', percentRM: 60, details: '60% Deadlift 1RM' },
      ]
    },
    {
      name: 'Strength Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Kettlebell Strict Press', sets: 4, reps: '10', rest: '0:00', rpe: 7, format: 'superset' },
        { name: 'Cable Fly', sets: 4, reps: '12', rest: '2:00', rpe: 6, format: 'superset' },
      ]
    },
    {
      name: 'Accessory Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Lateral Raise', sets: 3, reps: '15', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Face Pull', sets: 3, reps: '15', rest: '1:00', rpe: 6, format: 'superset', details: 'Rear delts + upper back — balances the pressing volume for shoulder health under load' },
      ]
    },
    {
      name: 'Accessory Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hammer Curl', sets: 4, reps: '15', rest: '0:00', rpe: 6, format: 'superset' },
        { name: 'Triceps Pressdown', sets: 4, reps: '15', rest: '1:00', rpe: 6, format: 'superset' },
      ]
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Dead Bug', sets: 3, reps: '10', rest: '1:00', rpe: 6, format: 'bodyweight', details: 'Per side — anti-extension trunk control' },
      ]
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '60s/side', format: 'recovery', details: 'Open the chest after pressing volume' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery', details: 'Lats + teres after rows and pulls' },
        { name: '90/90 Breathing', duration: '1-2 min', format: 'recovery', details: 'Down-regulate' },
      ]
    },
  ]
};

const trainingDay5: WorkoutTemplateDay = {
  name: 'Grinder PT',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '3:00', format: 'prep', details: 'Easy pace — raise core temp' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap prep for push-ups + pull-ups' },
        { name: 'Scapular Push-Ups', sets: 1, reps: '10', format: 'prep', details: 'Serratus + scap for the pressing volume' },
        { name: 'Spanish Squat Iso', sets: 1, reps: '30s', format: 'prep', details: 'Knee prime for the air-squat rounds' },
        { name: 'Leg Swings', sets: 1, reps: '10/side', format: 'prep', details: 'Open the hips before the runs' },
      ]
    },
    {
      name: 'Grinder PT Circuit',
      type: 'circuit',
      exercises: [
        { name: 'Push-Ups', sets: 5, reps: '20', rest: '0:00', rpe: 7, format: 'circuit', details: '5 Rounds' },
        { name: 'Air Squat', sets: 5, reps: '15', rest: '0:00', rpe: 7, format: 'circuit', details: '5 Rounds' },
        { name: 'Pull-Ups', sets: 5, reps: '10', rest: '0:00', rpe: 7, format: 'circuit', details: '5 Rounds' },
        { name: 'Hanging Knee Raise', sets: 5, reps: '15', rest: '0:00', rpe: 6, format: 'circuit', details: '5 Rounds — trades loaded spinal flexion for a stronger anti-flexion trunk demand' },
        { name: 'Run', sets: 5, distance: '200m', rest: '0:00', rpe: 7, format: 'circuit', details: '5 Rounds' },
      ]
    },
    {
      name: 'Finisher',
      type: 'conditioning',
      exercises: [
        { name: 'Kettlebell Snatch', sets: 3, reps: '10', rest: '1:30', rpe: 7, format: 'weight-reps', details: 'Per side' },
        { name: 'Diamond Push-Ups', sets: 3, reps: '15', rest: '1:00', rpe: 7, format: 'bodyweight' },
        { name: 'Dumbbell Curl', sets: 4, reps: '15', rest: '1:00', rpe: 6, format: 'weight-reps' },
        { name: 'Overhead Tricep Extension', sets: 4, reps: '15', rest: '1:00', rpe: 6, format: 'weight-reps' },
      ]
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Chest after all the pressing' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hips + quads after the squats and runs' },
        { name: 'Lat Stretch', duration: '45s/side', format: 'recovery', details: 'Lats after the pull-ups' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ]
    },
  ]
};

const trainingDay6: WorkoutTemplateDay = {
  name: 'Ruck',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Ruck Prep',
      type: 'prep',
      exercises: [
        { name: 'Walk', sets: 1, duration: '3:00', format: 'prep', details: 'Easy pace to warm up' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Ankles carry the load every step' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips for a loaded stride' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Glutes drive the ruck — wake them up' },
        { name: 'Calf Raise', sets: 2, reps: '15', format: 'prep', details: 'Prep ankles + calves for load' },
        { name: 'Dead Bug', sets: 1, reps: '8/side', format: 'prep', details: 'Trunk bracing under the pack' },
      ]
    },
    {
      name: 'Ruck March',
      type: 'ruck',
      exercises: [
        { name: 'Ruck March', sets: 1, distance: '3 miles', format: 'ruck', rpe: 6, details: '35 lbs. Week 2 of the ramp to the wk6 test. Keep pace conversational (Z2).' },
      ]
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — calves take the load' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after the march' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Spinal decompression after time under the pack' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ]
    },
  ]
};


/**
 * RUCK RAMP — authored as DATA, not prose.
 *
 * trainingDay6 is reused for weeks 2-5, so its single `distance: '3 miles'`
 * was what every week actually prescribed. The 3 -> 4 -> 5 -> 6 mile ramp to
 * the week-6 test existed only inside the `details` STRING, where no engine
 * could read it: the card said 3 miles every week while the text promised a
 * ramp. Nothing in the adaptive layer could rescue it either — aerobic
 * progression is bounded at +10% per session, and this ramp averages ~19%.
 *
 * Week 2 uses trainingDay6 as authored; 3-5 override the distance.
 */
function ruckDay(miles: number, weekNote: string): WorkoutTemplateDay {
  return {
    ...trainingDay6,
    blocks: trainingDay6.blocks.map(b =>
      b.name !== 'Ruck March' ? b : {
        ...b,
        exercises: b.exercises.map(e =>
          e.name !== 'Ruck March' ? e : {
            ...e,
            distance: `${miles} miles`,
            details: `35 lbs. ${weekNote} of the ramp to the wk6 test. Keep pace conversational (Z2).`,
          }),
      }),
  };
}

const trainingDay7: WorkoutTemplateDay = {
  name: 'Recovery',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Active Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Yoga', sets: 1, duration: '30-60 min', format: 'mobility', details: 'Yoga with Adrienne on YouTube' },
      ]
    },
  ]
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const specialForcesSustainment6Week: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    // Week 1 & Week 6: Assessment Weeks (uses testing-week-day keys)
    'testing-week-day1': assessmentDay1,
    'testing-week-day2': restDay, // Rest day between assessments
    'testing-week-day3': assessmentDay3,
    'testing-week-day4': restDay, // Rest day between assessments
    'testing-week-day5': assessmentDay5,
    'testing-week-day6': assessmentDay6,
    'testing-week-day7': restDay, // Rest day
    
    // Weeks 2-5: Training Cycle (training days)
    // Week-specific ruck days override the ramp distance (see ruckDay above).
    'week3-day6': ruckDay(4, 'Week 3'),
    'week4-day6': ruckDay(5, 'Week 4'),
    'week5-day6': ruckDay(6, 'Week 5'),
    'training-day1': trainingDay1,
    'training-day2': trainingDay2,
    'training-day3': trainingDay3,
    'training-day4': trainingDay4,
    'training-day5': trainingDay5,
    'training-day6': trainingDay6,
    'training-day7': trainingDay7,
  },
};
